import React, { useState } from "react";
import { Card, DataTable, Thumbnail, Link, EmptyState } from "@shopify/polaris";
import MultiLineText from "../MultiLineText";
import { Inspection, Status, Type } from "../../types/Inspection";
import styles from "./InspectionsTable.module.css";
import CheckListIcon from "./CheckListIcon";

interface Props {
    data: Inspection[];
}

const DEFAULT_SHIP_IMAGE = "/images/blank_mainship.jpg";

export default function InspectionsTable({ data }: Props) {
    const [rows, setRows] = useState([[]]);

    function buildRow(inspection: Inspection) {
        if (
            inspection.MainShipImage === null ||
            inspection.MainShipImage === ""
        ) {
            inspection.MainShipImage = DEFAULT_SHIP_IMAGE;
        }

        inspection.InspectionType = Type[inspection.InspectionTypeId];
        inspection.InspectionStatus = Status[inspection.Status];

        const formatDate = (date: string) => {
            if (date == null) {
                return "N/A";
            }

            return new Date(date).toLocaleDateString("en-GB", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric"
            });
        };

        let checklistCountHtml = (
            <p className={styles.semiBoldCenter}>
                {inspection.NumberOfChecklists}
            </p>
        );
        let documentCountHtml = (
            <p className={styles.semiBoldCenter}>
                {inspection.NumberOfDocuments}
            </p>
        );
        let imageCountHtml = (
            <p className={styles.semiBoldCenter}>{inspection.NumberOfPhotos}</p>
        );

        const surveyorCompletionStatus = inspection.SurveyorCompletionStatus;

        if (
            surveyorCompletionStatus !== undefined &&
            surveyorCompletionStatus !== null
        ) {
            if (surveyorCompletionStatus.ChecklistComplete) {
                checklistCountHtml = <CheckListIcon hasCheckList={true} />;
            }

            if (surveyorCompletionStatus.DocumentsComplete) {
                documentCountHtml = <CheckListIcon hasCheckList={true} />;
            }

            if (surveyorCompletionStatus.PhotosComplete) {
                imageCountHtml = <CheckListIcon hasCheckList={true} />;
            }
        }

        return [
            <p className={styles.bold}>{inspection.InspectionNumber}</p>,
            <Thumbnail
                source={inspection.MainShipImage}
                alt="Inspection Main Ship Image"
            />,
            <Link url={"/inspection/" + inspection.Id}>
                <MultiLineText
                    primary={inspection.ShipName}
                    secondary={inspection.ShipIMO}
                />
            </Link>,
            <p className={styles.semiBold}>{inspection.InspectionType}</p>,
            <p className={styles.semiBold}>
                {formatDate(inspection.DateTimeFrom)}
            </p>,
            <p className={styles.semiBold}>{inspection.DueDate}</p>,
            <div>{checklistCountHtml}</div>,
            <div>{documentCountHtml}</div>,
            <div>{imageCountHtml}</div>
        ];
    }

    React.useEffect(() => {
        const inspectionRowArray = [] as [][];

        data.forEach((inspection) => {
            const row = buildRow(inspection) as [];
            inspectionRowArray.push(row);
        });

        setRows(inspectionRowArray);
    }, [data]);

    return (
        <Card>
            {rows.length > 0 ? (
                <DataTable
                    verticalAlign="middle"
                    columnContentTypes={[
                        "text",
                        "text",
                        "text",
                        "text",
                        "text",
                        "text",
                        "text",
                        "text",
                        "text"
                    ]}
                    headings={[
                        <p className={styles.semiBold}>Idwal ID</p>,
                        "",
                        <p className={styles.semiBold}>Vessel</p>,
                        <p className={styles.semiBold}>Type</p>,
                        <p className={styles.semiBold}>Inspection Date</p>,
                        <p className={styles.semiBold}>Due Date</p>,
                        <p className={styles.semiBoldCenter}>Checklists</p>,
                        <p className={styles.semiBoldCenter}>Documents</p>,
                        <p className={styles.semiBoldCenter}>Photos</p>
                    ]}
                    rows={rows}
                />
            ) : (
                <div>
                    <EmptyState
                        image=""
                        fullWidth
                        heading="You don't have any inspections."
                    >
                        <p>
                            Please{" "}
                            <a href="https://www.idwalmarine.com/contact-us">
                                Contact
                            </a>{" "}
                            us if something is missing!
                        </p>
                    </EmptyState>
                </div>
            )}
        </Card>
    );
}
